import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { AlertCircle, ArrowLeft, CheckCircle2, Loader2 } from "lucide-react";
import { api, formatApiError } from "../../api";
import { MEMBER } from "../../constants/testIds";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";

// UX-MEM-01 §1 SCR-P03-04 + highest-risk rule "payment must show an unambiguous success OR
// failure screen every time, never a spinner that just times out": both outcomes below are
// distinct, full-screen, and clearly labeled. `SIMULATE_FAIL` is a documented test-only sentinel
// (see routers/member_app.py) — no real UPI gateway is integrated this phase, so this is the
// deterministic way to prove the failure path actually renders instead of leaving it untested.
export default function Pay() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const maxAmount = (Number(params.get("amount")) || 0) / 100;
  const [amount, setAmount] = useState(String(maxAmount));
  const [railRef, setRailRef] = useState(`tok_upi_${Date.now()}`);
  const [result, setResult] = useState(null); // "success" | "failure" | null

  const payMutation = useMutation({
    mutationFn: () => api.post("/member/payments", { amount: Math.round(Number(amount) * 100), purpose: params.get("purpose") || "dues", rail_ref: railRef }),
    onSuccess: () => setResult("success"),
    onError: () => setResult("failure"),
  });

  if (result) {
    const ok = result === "success";
    return (
      <div data-testid={ok ? MEMBER.paySuccessScreen : MEMBER.payFailureScreen} className="fixed inset-0 z-[70] flex flex-col items-center justify-center px-8 text-center md:max-w-md md:mx-auto" style={{ background: "#0A0A0A" }}>
        <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 200 }}>
          {ok ? <CheckCircle2 size={72} color="#32D74B" strokeWidth={1.5} /> : <AlertCircle size={72} color="#D6202B" strokeWidth={1.5} />}
        </motion.div>
        <h1 className="font-heading text-3xl font-black mt-6">{ok ? "Payment successful" : "Payment failed"}</h1>
        <p className="text-sm mt-2" style={{ color: "#A1A1AA" }}>{ok ? `₹${amount} paid.` : payMutation.error ? formatApiError(payMutation.error).message : "The payment rail declined this transaction."}</p>
        <Button data-testid={MEMBER.payBackButton} onClick={() => navigate("/member/membership")} className="w-full mt-10 text-white active:scale-95 transition-transform" style={{ background: "#D6202B" }}>
          Back to membership
        </Button>
      </div>
    );
  }

  return (
    <div data-testid={MEMBER.payPage} className="space-y-5">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm" style={{ color: "#A1A1AA" }}><ArrowLeft size={14} /> Back</button>
      <h1 className="font-heading text-2xl font-bold">Pay {params.get("purpose") === "renew" ? "renewal" : "dues"}</h1>

      <div className="rounded-2xl p-5 space-y-3" style={{ background: "#141414", border: "1px solid rgba(255,255,255,0.1)" }}>
        <label className="text-xs" style={{ color: "#A1A1AA" }}>Amount (₹) <span style={{ color: "#D6202B" }}>*</span></label>
        <Input
          data-testid={MEMBER.payAmountInput} type="number" value={amount}
          onChange={(e) => setAmount(e.target.value === "" ? "" : String(Math.min(Number(e.target.value) || 0, maxAmount)))}
          className="bg-[#0A0A0A] border-white/10 text-white"
        />
        <p className="text-xs" style={{ color: "#A1A1AA" }}>Up to ₹{maxAmount} due — excess amounts aren't accepted.</p>
        <label className="text-xs" style={{ color: "#A1A1AA" }}>Payment reference</label>
        <Input data-testid={MEMBER.payRailInput} value={railRef} onChange={(e) => setRailRef(e.target.value)} className="bg-[#0A0A0A] border-white/10 text-white font-mono text-xs" />
        <button type="button" onClick={() => setRailRef("SIMULATE_FAIL")} className="text-xs underline" style={{ color: "#A1A1AA" }}>(test) simulate a declined payment</button>
      </div>

      <Button data-testid={MEMBER.paySubmitButton} disabled={!amount || Number(amount) <= 0 || payMutation.isPending} onClick={() => payMutation.mutate()} className="w-full h-12 text-white font-semibold active:scale-95 transition-transform" style={{ background: "#D6202B" }}>
        {payMutation.isPending ? <Loader2 size={16} className="animate-spin" /> : `Pay ₹${amount || 0}`}
      </Button>
    </div>
  );
}
